import { StatusPillVariant } from '../../../../shared/ui/status-pill/status-pill.component';
import { investmentPaymentLabel, investmentPaymentVariant } from '../../shared/investment-payment-status.util';

export type InvestmentAssetType = 1 | 2;

export const INVESTMENT_ASSET_INTANGIBLE: InvestmentAssetType = 1;
export const INVESTMENT_ASSET_MATERIAL: InvestmentAssetType = 2;

export const INVESTMENT_ASSET_EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

export const INVESTMENT_ASSET_TYPE_INVESTMENT = 'Tabla AEAT';

export interface InvestmentAssetKind {
  type: InvestmentAssetType;
  icon: string;
  title: string;
  subTitle: string;
  singular: string;
  filterView: string;
  listEndpoint: string;
  searchPlaceholder: string;
}

export const INVESTMENT_ASSET_KINDS: Record<InvestmentAssetType, InvestmentAssetKind> = {
  1: {
    type: 1,
    icon: 'class',
    title: 'Inmovilizado intangible',
    subTitle: 'Consulta, paga y amortiza tus inversiones en inmovilizado intangible',
    singular: 'inmovilizado intangible',
    filterView: 'investmentAssetIntangible',
    listEndpoint: '/api/v2/investment-asset-intangibles',
    searchPlaceholder: 'Buscar proveedor, descripción, cuenta…',
  },
  2: {
    type: 2,
    icon: 'business',
    title: 'Inmovilizado material',
    subTitle: 'Consulta, paga y amortiza tus inversiones en inmovilizado material',
    singular: 'inmovilizado material',
    filterView: 'investmentAssetMaterial',
    listEndpoint: '/api/v2/investment-asset-materials',
    searchPlaceholder: 'Buscar proveedor, descripción, cuenta…',
  },
};

export interface InvestmentAssetRow {
  uid: string;
  type: string;
  supplier: string;
  description: string;
  account: string;
  purchaseDate: string;
  startWorkingDate: string;
  totalWithoutTax: number;
  totalTax: number;
  total: number;
  paid: number;
  pending: number;
  paymentDate: string;
  image: boolean;
  pdf: boolean;
  amortisationYearExists: boolean;
  maximumLinealCoeficient: number;
  maximumPeriod: number;
  typeInvestment: string;
  fiscalYear: number;
  __expanded?: boolean;
  __selected?: boolean;
}

export interface InvestmentAssetListSummary {
  count: number;
  totalWithoutTax: number;
  totalTax: number;
  total: number;
  paid: number;
  pending: number;
}

export interface InvestmentAssetTax {
  uid: string;
  investmentId?: number;
  taxId: number;
  name: string;
  taxImport: number;
  type: string;
}

export interface InvestmentAssetRecord {
  uid?: string;
  type?: string;
  activityUid?: string;
  provider: string;
  elementDescription: string;
  accountActivity: string;
  dateBuy: string | Date;
  dateStartWorking: string | Date;
  lifetime?: string;
  totalWithoutTax: number;
  paided?: string | Date;
  residualValue?: number;
  typeInvestment: string;
  maximumLinealCoeficient: number;
  maximumPeriod: number;
  fiscalYear?: number;
  image: boolean;
  pdf: boolean;
  fileUid: string;
  typePay?: string;
  typePayBankAccount?: string;
  taxes: InvestmentAssetTax[];
  contactsUid: string;
  manualMovement?: any;
  projectUid?: string | null;
}

export interface InvestmentAssetDeposit {
  uid: number;
  investmentsUid: string;
  paymentCardsUid: string;
  bankAccountsUid: string;
  amount: number;
  depositDate: string | Date;
  date: string;
  typePay: string;
  concepto: string;
  commissions: number;
  total: number;
  totalDeposit: number;
  bankAccountInfo: string;
  efectivoId: string;
  partnersId: number;
  polizaCreditoId: string;
  tableName: string;
  conciliationId: number | null;
}

export interface InvestmentAssetAmortizationLine {
  name: string;
  accountActivity: string;
  year: number;
  month: string;
  amount: number;
  total: number;
}

export interface InvestmentAssetAmortizationEntry {
  accountEntry: number;
  date: string;
  amount: number;
  description: string;
}

export interface InvestmentAssetDetail {
  deposits: InvestmentAssetDeposit[];
  amortization: InvestmentAssetAmortizationLine[];
  amortizationEntries: InvestmentAssetAmortizationEntry[];
}

export interface InvestmentAssetCoefficient {
  maximumLinearCoefficient: number;
  maximumPeriodYears: number;
}

export interface InvestmentAssetPayTarget {
  uid: string;
  total: number;
  description?: string;
  supplier?: string;
}

export function roundMoney(value: number): number {
  return Math.round((Number(value) || 0) * 100) / 100;
}

export function hasApiDate(value: any): boolean {
  if (!value) return false;
  const date = value instanceof Date ? value : new Date(value);
  return !isNaN(date.getTime()) && date.getFullYear() > 1;
}

export function paymentVariant(row: InvestmentAssetRow): StatusPillVariant {
  return investmentPaymentVariant(row ? { total: row.total, paid: row.paid, pending: row.pending } : null);
}

export function paymentLabel(row: InvestmentAssetRow): string {
  return investmentPaymentLabel(row ? { total: row.total, paid: row.paid, pending: row.pending, paidDate: row.paymentDate } : null);
}

export function isNonDepreciable(row: InvestmentAssetRow): boolean {
  return !!row && String(row.account || '').trim().startsWith('210');
}

export function amortisationVariant(row: InvestmentAssetRow): StatusPillVariant {
  return row && row.amortisationYearExists ? 'success' : 'neutral';
}

export function amortisationLabel(row: InvestmentAssetRow): string {
  if (isNonDepreciable(row)) return 'No amortizable';
  return row && row.amortisationYearExists ? 'Contabilizada' : 'Sin contabilizar';
}
